import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Flag, Check, X, Eye, AlertTriangle, MessageSquare, Star, Clock, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import AdminLayout from './AdminLayout';

interface Report {
  id: string;
  reporterId: string;
  targetType: 'review' | 'comment';
  targetId: string;
  reason: string;
  details: string | null;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reporter?: {
    id: string;
    displayName: string | null;
    avatar: string | null;
  };
  targetContent?: {
    text?: string;
    rating?: number;
    restaurantName?: string;
    authorName?: string;
  };
}

interface ReportStats {
  pending: number;
  reviewed: number;
  resolved: number;
  dismissed: number;
  total: number;
}

const reasonLabels: Record<string, string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate: 'Hate Speech',
  inappropriate: 'Inappropriate Content',
  other: 'Other',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500',
  reviewed: 'bg-blue-500',
  resolved: 'bg-green-500',
  dismissed: 'bg-gray-500',
};

function StatCard({ title, value, icon: Icon, color }: { title: string; value: number; icon: any; color: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className={`p-2 rounded-full ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminReports() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('all');

  const { data: stats, isLoading: statsLoading } = useQuery<ReportStats>({
    queryKey: ['/api/admin/reports/stats'],
  });

  const { data: reportsData, isLoading: reportsLoading } = useQuery<{ reports: Report[]; total: number }>({
    queryKey: ['/api/admin/reports', statusFilter, typeFilter, reasonFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('targetType', typeFilter);
      if (reasonFilter !== 'all') params.set('reason', reasonFilter);
      const res = await fetch(`/api/admin/reports?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch reports');
      return res.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, action }: { id: string; status: string; action?: string }) => {
      return apiRequest('PATCH', `/api/admin/reports/${id}`, { status, action });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reports'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reports/stats'] });
      toast({ title: 'Report updated', description: 'The report status has been updated.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update report.', variant: 'destructive' });
    },
  });

  const handleDismiss = (reportId: string) => {
    updateStatusMutation.mutate({ id: reportId, status: 'dismissed' });
  };

  const handleResolve = (reportId: string, action?: string) => {
    updateStatusMutation.mutate({ id: reportId, status: 'resolved', action });
  };

  const reports = reportsData?.reports || [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Report Management</h1>
          <p className="text-muted-foreground">Review and manage user-submitted reports for reviews and comments</p>
        </div>

        {statsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard title="Pending" value={stats?.pending || 0} icon={Clock} color="bg-yellow-500" />
            <StatCard title="Reviewed" value={stats?.reviewed || 0} icon={Eye} color="bg-blue-500" />
            <StatCard title="Resolved" value={stats?.resolved || 0} icon={Check} color="bg-green-500" />
            <StatCard title="Dismissed" value={stats?.dismissed || 0} icon={X} color="bg-gray-500" />
            <StatCard title="Total" value={stats?.total || 0} icon={Flag} color="bg-[#0095a5]" />
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>Reports</CardTitle>
                <CardDescription>
                  {reportsData?.total || 0} reports found
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="review">Reviews</SelectItem>
                    <SelectItem value="comment">Comments</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={reasonFilter} onValueChange={setReasonFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reasons</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                    <SelectItem value="harassment">Harassment</SelectItem>
                    <SelectItem value="hate">Hate Speech</SelectItem>
                    <SelectItem value="inappropriate">Inappropriate</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reportsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Flag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No reports found matching your filters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <Card key={report.id} className="hover-elevate">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className={statusColors[report.status]}>
                              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                            </Badge>
                            <Badge variant="outline">
                              {report.targetType === 'review' ? (
                                <><Star className="w-3 h-3 mr-1" /> Review</>
                              ) : (
                                <><MessageSquare className="w-3 h-3 mr-1" /> Comment</>
                              )}
                            </Badge>
                            <Badge variant="secondary">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {reasonLabels[report.reason] || report.reason}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {report.createdAt && formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Reported by:</span>
                            <div className="flex items-center gap-2">
                              <Avatar className="w-5 h-5">
                                <AvatarImage src={report.reporter?.avatar || ''} />
                                <AvatarFallback className="text-[10px]">
                                  {(report.reporter?.displayName || 'U')[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span>{report.reporter?.displayName || 'Unknown User'}</span>
                            </div>
                          </div>

                          {report.targetContent && (
                            <div className="bg-muted/50 rounded-md p-3 space-y-1">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                {report.targetType === 'review' ? (
                                  <>
                                    <Star className="w-4 h-4" />
                                    <span>Review for: {report.targetContent.restaurantName || 'Unknown Restaurant'}</span>
                                    {report.targetContent.rating && (
                                      <Badge variant="outline" className="ml-2">
                                        {report.targetContent.rating} stars
                                      </Badge>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <MessageSquare className="w-4 h-4" />
                                    <span>Comment by: {report.targetContent.authorName || 'Unknown User'}</span>
                                  </>
                                )}
                              </div>
                              <p className="text-sm line-clamp-3">{report.targetContent.text || 'No content available'}</p>
                            </div>
                          )}

                          {report.details && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Additional details: </span>
                              <span>{report.details}</span>
                            </div>
                          )}
                        </div>

                        {report.status === 'pending' && (
                          <div className="flex flex-row md:flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResolve(report.id, 'flag')}
                              disabled={updateStatusMutation.isPending}
                            >
                              <Flag className="w-4 h-4 mr-1" />
                              Flag Content
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResolve(report.id, 'reject')}
                              disabled={updateStatusMutation.isPending}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject Content
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDismiss(report.id)}
                              disabled={updateStatusMutation.isPending}
                            >
                              Dismiss
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
